import React, { useState, useContext } from 'react';
import { AuthContext } from '../store/ContextStore';
import Spinner from './Spinner';

function ResetPasswordForm(props: any) {
    const { handleResetPassword, password, setPassword, confirmPassword, setConfirmPassword, isPending } = props || {};
    const { error } = useContext(AuthContext);

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [validationResults, setValidationResults] = useState({
        length: false,
        uppercaseLowercase: false,
        numberSpecialChar: false,
        passwordsMatch: false,
    });

    const validatePassword = (password: string, confirmPassword: string) => {
        setValidationResults({
            length: password.length >= 8,
            uppercaseLowercase: /[A-Z]/.test(password) && /[a-z]/.test(password),
            numberSpecialChar: /\d/.test(password) && /[!@#$%^&*(),.?":{}|<>]/.test(password),
            passwordsMatch: password === confirmPassword && password !== "" && confirmPassword !=="",
        });
    };

    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newPassword = e.target.value;
        setPassword(newPassword);
        validatePassword(newPassword, confirmPassword);
    };

    const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newConfirmPassword = e.target.value;
        setConfirmPassword(newConfirmPassword);
        validatePassword(password, newConfirmPassword);
    };

    return (
        <form
            onSubmit={(e) => {
                e.preventDefault();
                handleResetPassword();
            }}
            className="w-full max-w-md"
        >
            <div className="flex flex-col grow max-md:mt-5 bg-white p-10 rounded-lg shadow-2xl">
                <h2 className="text-2xl font-semibold text-center text-gray-800 mb-6">Reset Your Password</h2>
                
                <p className="text-sm text-gray-600 text-center mb-8">
                    Please set a new password for your account
                </p>

                {/* Password Input */}
                <div className="w-full mb-4">
                    <label htmlFor="password" className="block text-sm font-medium text-gray-600 mb-1">
                        Password
                    </label>
                    <div className="relative">
                        <input
                            id="password"
                            type={showPassword ? "text" : "password"}
                            placeholder="Enter your password"
                            value={password}
                            onChange={handlePasswordChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-cyan-500"
                        />
                        <button 
                            type="button"
                            className="absolute inset-y-0 right-0 pr-3 flex items-center"
                            onClick={() => setShowPassword(!showPassword)}
                        >
                            <img
                                src={showPassword ? "/visible.svg" : "/invisible.svg"}
                                alt={showPassword ? "Hide password" : "Show password"}
                                className="w-4 h-4"
                            />
                        </button>
                    </div>
                </div>

                {/* Confirm Password Input */}
                <div className="w-full mb-4">
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-600 mb-1">
                        Confirm Password
                    </label>
                    <div className="relative">
                        <input
                            id="confirmPassword"
                            type={showConfirmPassword ? "text" : "password"}
                            placeholder="Re-enter your password"
                            value={confirmPassword}
                            onChange={handleConfirmPasswordChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-cyan-500"
                        />
                        <button 
                            type="button"
                            className="absolute inset-y-0 right-0 pr-3 flex items-center"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        >
                            <img
                                src={showConfirmPassword ? "/visible.svg" : "/invisible.svg"}
                                alt={showConfirmPassword ? "Hide password" : "Show password"}
                                className="w-4 h-4"
                            />
                        </button>
                    </div>
                </div>

                {/* Password Validation Messages */}
                <div className="w-full space-y-1.5 text-xs mb-4">
                    <div className={`flex items-center ${validationResults.length ? "text-green-600" : "text-gray-500"}`}>
                        <svg className="w-3.5 h-3.5 mr-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span>At least 8 characters</span>
                    </div>
                    <div className={`flex items-center ${validationResults.uppercaseLowercase ? "text-green-600" : "text-gray-500"}`}>
                        <svg className="w-3.5 h-3.5 mr-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span>At least 1 uppercase & 1 lowercase character</span>
                    </div>
                    <div className={`flex items-center ${validationResults.numberSpecialChar ? "text-green-600" : "text-gray-500"}`}>
                        <svg className="w-3.5 h-3.5 mr-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span>At least 1 number & 1 special character</span>
                    </div>
                    <div className={`flex items-center ${validationResults.passwordsMatch ? "text-green-600" : "text-gray-500"}`}>
                        <svg className="w-3.5 h-3.5 mr-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span>Passwords must match</span>
                    </div>
                </div>

                {/* Reset Button */}
                <button
                    type="submit"
                    disabled={isPending || !validationResults.length || !validationResults.uppercaseLowercase || !validationResults.numberSpecialChar || !validationResults.passwordsMatch}
                    className={`w-full py-2.5 px-4 border border-transparent rounded text-sm font-medium text-white ${
                        validationResults.length && validationResults.uppercaseLowercase && validationResults.numberSpecialChar && validationResults.passwordsMatch
                            ? "bg-cyan-600 hover:bg-cyan-700 focus:outline-none"
                            : "bg-gray-300 cursor-not-allowed"
                    }`}
                >
                    {isPending ? <Spinner /> : "Reset Password"}
                </button>

                {error && (
                    <div className="mt-3 p-3 bg-red-100 text-red-700 border border-red-200 rounded-md text-sm font-medium">
                        {typeof error === 'string' ? error : error.message || JSON.stringify(error)}
                    </div>
                )}
            </div>
        </form>
    );
}

export default ResetPasswordForm;
